import { memo, useCallback, useMemo } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import { colors } from "../utils/const";
import { styles } from "./page_cover";
import { useI18n } from "@/i18n/I18nProvider";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

// Split Form from the main PageCover bundle.
// Keep a placeholder to avoid layout shift.
const Form = dynamic(() => import("./Form.jsx"), {
  loading: () => (
    <Box
      sx={{
        minHeight: { xs: 420, md: 480 },
        width: "100%",
      }}
    />
  ),
});

function PageCover({
  title1,
  title2,
  description,
  img,
  color,
  formType,
  product,
  source,
    flipImg =true,
}) {
  const router = useRouter();
  const { t, locale } = useI18n();

  const isContactNow = product === "shopping";
  const isEnglish = locale === "en";

  const flipDirection = isEnglish ? "scaleX(-1)" : "scaleX(1)";
  const textDirection = isEnglish ? "left" : "right";

  const decoratorPosition = useMemo(
    () => (isEnglish ? { right: "0px" } : { left: "0px" }),
    [isEnglish]
  );

  const handleContactNowClick = useCallback(() => {
    const isLogin =
      typeof window !== "undefined" && window.localStorage.getItem("UT_UI");

    router.push(isLogin ? "/contract" : "/login");
  }, [router]);

  return (
    <Box
      component="section"
      sx={styles.container}
      style={{
        backgroundColor: isContactNow ? color : "#D9D9D9",
        position: "relative",
      }}
    >
      {/* Decorative image - not LCP, no priority */}
      <Box
        aria-hidden="true"
        sx={{
          width: { xs: "200px", md: "300px", lg: "400px" },
          height: { xs: "100px", md: "200px", lg: "300px" },
          position: "absolute",
          top: "0px",
          ...decoratorPosition,
          opacity: ".2",
          transform: flipDirection,
          pointerEvents: "none",
        }}
      >
        <Image
          src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/decorator.webp"
          alt=""
          fill
          sizes="(max-width: 600px) 200px, (max-width: 900px) 300px, 400px"
          style={{ objectFit: "cover" }}
          quality={50}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          textAlign: textDirection,
          m: 3,
        }}
      >
        {isContactNow && (
          <Box sx={{ position: "relative", width: 100, height: 56 }}>
            <Image
              src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/contact-now.webp"
              alt="Contact Now"
              fill
              sizes="100px"
              style={{ objectFit: "contain" }}
            />
          </Box>
        )}

        {/* Remove AOS from above-the-fold title to avoid delaying visible paint */}
        <Box sx={styles.container}>
          <Box sx={{ position: "relative", display: "inline-block" }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "35px", md: isContactNow ? "30px" : "42px" },
                whiteSpace: "pre-wrap",
                fontWeight: "bold",
                color: isContactNow ? "#fff" : "#2D317F",
                pb: 1,
                maxWidth: { md: "550px" },
                lineHeight: 1.5,
              }}
            >
              {title1}
            </Typography>

            {/* Decorative underline - do not eager load */}
            <Box
              component="img"
              alt=""
              aria-hidden="true"
              src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/line-main.webp"
              loading="lazy"
              decoding="async"
              style={{
                position: "absolute",
                left: 0,
                bottom: 0,
                width: "100%",
                height: "5px",
                objectFit:
                  title1 === "Auto" ||
                  title1 === "Trucks" ||
                  title1 === "Clubs"
                    ? "contain"
                    : "cover",
                transform: flipDirection,
                display: "block",
                pointerEvents: "none",
              }}
            />
          </Box>
        </Box>

        <Typography
          variant="h2"
          sx={{
            fontSize: isContactNow ? "15px" : "30px",
            fontWeight: isContactNow ? "initial" : "bold",
            color: isContactNow ? "#fff" : "#2D317F",
            maxWidth: "600px",
          }}
        >
          {title2}
        </Typography>

        <Box
          sx={{
            color: isContactNow ? "rgba(255, 255, 255, 0.7)" : "#404456",
            fontSize: "16px",
            wordWrap: "break-word",
            maxWidth: "600px",
            pb: 2,
            lineHeight: 2,
            textAlign: { lg: textDirection },
          }}
        >
          {description}
        </Box>

        {isContactNow && (
          <Box
            onClick={handleContactNowClick}
            component={Button}
            sx={{
              backgroundColor: colors.orange,
              color: "#fff",
              transition: ".5s",
              "&:hover": {
                backgroundColor: colors.orange,
                color: "#fff",
                opacity: ".7",
              },
            }}
          >
            {t.contact_now_btn}
          </Box>
        )}

        {/* Real hero image - likely LCP */}
        <Box
          sx={{
            maxWidth: {
              xs: isContactNow ? "300px" : "100%",
              sm: isContactNow ? "300px" : "500px",
            },
            m: { xs: 1, sm: 5 },
            mt: "0px !important",

transform: flipImg ? flipDirection : "none",
            position: "relative",
            width: "100%",
            aspectRatio: "16 / 9",
          }}
        >
          <Image
            src={img}
            alt={title1 || "Product cover"}
            fill
            priority
            sizes={
              isContactNow
                ? "(max-width: 600px) 300px, 300px"
                : "(max-width: 600px) 100vw, 500px"
            }
            style={{ objectFit: "contain" }}
          />
        </Box>
      </Box>

      <Form color={color} type={formType} product={product} source={source} />
    </Box>
  );
}

export default memo(PageCover);